import { useEffect, useState, useRef } from 'react';

interface InkSpreadTextProps {
  text: string;
  isStreaming?: boolean;
  className?: string;
}

/**
 * InkSpreadText - Renders text with an ink spreading effect
 * Each word appears with a blur-to-sharp animation like ink bleeding onto paper
 */
export default function InkSpreadText({ text, isStreaming = false, className = '' }: InkSpreadTextProps) {
  const [displayedWords, setDisplayedWords] = useState<string[]>([]);
  const [revealedCount, setRevealedCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousTextRef = useRef(text);

  useEffect(() => {
    const words = text.split(/(\s+)/); // Keep whitespace as separate elements

    if (isStreaming) {
      // During streaming, track new words
      const prevWords = previousTextRef.current.split(/(\s+)/);
      const newWordsCount = words.length - prevWords.length;

      if (newWordsCount > 0) {
        // New words arrived - animate them
        setDisplayedWords(words);
        setRevealedCount(prevWords.length);

        // Gradually reveal new words
        const revealInterval = setInterval(() => {
          setRevealedCount(prev => {
            if (prev >= words.length) {
              clearInterval(revealInterval);
              return prev;
            }
            return prev + 1;
          });
        }, 60);

        return () => clearInterval(revealInterval);
      } else {
        setDisplayedWords(words);
      }
    } else {
      // Not streaming - show all words with staggered animation
      setDisplayedWords(words);
      setRevealedCount(0);

      // Animate all words in sequence
      const revealInterval = setInterval(() => {
        setRevealedCount(prev => {
          if (prev >= words.length) {
            clearInterval(revealInterval);
            return prev;
          }
          return prev + 1;
        });
      }, 40);

      return () => clearInterval(revealInterval);
    }

    previousTextRef.current = text;
  }, [text, isStreaming]);

  // Reset when text is cleared
  useEffect(() => {
    if (!text) {
      setDisplayedWords([]);
      setRevealedCount(0);
    }
  }, [text]);

  return (
    <div ref={containerRef} className={`ink-spread-container leading-relaxed ${className}`}>
      {displayedWords.map((word, index) => {
        // Check if this is whitespace
        if (/^\s+$/.test(word)) {
          return <span key={index}>{word}</span>;
        }

        const isRevealed = index < revealedCount;
        const isAnimating = index >= revealedCount && index < revealedCount + 10;

        return (
          <span
            key={index}
            className={isRevealed ? 'ink-revealed' : isAnimating ? 'ink-spread-word' : 'opacity-0'}
            style={isAnimating ? { animationDelay: `${(index - revealedCount) * 50}ms` } : undefined}
          >
            {word}
          </span>
        );
      })}
      {isStreaming && (
        <span className="inline-block w-0.5 h-5 bg-accent ml-1 animate-pulse" />
      )}
    </div>
  );
}
